import { supabase } from '@/integrations/supabase/client';
import type {
  ContentFolder,
  ContentImage,
} from '../types/content.types';
import {
  mapDbRowToContentFolder,
  mapDbRowToContentImage,
} from '../services/content.mappers';
import {
  supabaseContentStorage,
  getImageDimensions,
} from '../infrastructure/supabaseContentStorage';

export class ContentGalleryRepository {
  /**
   * Fetch folders for a campaign scoped by organization.
   */
  async getFolders(orgId: string, campaignId: string): Promise<ContentFolder[]> {
    if (!campaignId) return [];

    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data, error } = await supabase
      .from('content_folders')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapDbRowToContentFolder);
  }

  /**
   * Create a folder for a campaign.
   */
  async createFolder(
    orgId: string,
    campaignId: string,
    name: string
  ): Promise<ContentFolder> {
    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data, error } = await supabase
      .from('content_folders')
      .insert({
        campaign_id: campaignId,
        name,
        organization_id: orgId,
      })
      .select()
      .single();

    if (error) throw error;
    return mapDbRowToContentFolder(data);
  }

  /**
   * Delete a folder.
   */
  async deleteFolder(orgId: string, folderId: string): Promise<void> {
    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { error } = await supabase
      .from('content_folders')
      .delete()
      .eq('id', folderId);

    if (error) throw error;
  }

  /**
   * Fetch images for a campaign scoped by organization.
   */
  async getImages(orgId: string, campaignId: string): Promise<ContentImage[]> {
    if (!campaignId) return [];

    await supabase.rpc('set_current_organization', { org_id: orgId });

    const { data, error } = await supabase
      .from('content_images')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(mapDbRowToContentImage);
  }

  /**
   * Upload image to Supabase Storage and persist metadata.
   */
  async uploadImage(params: {
    file: File;
    organizationId: string;
    campaignId: string;
    folderId?: string;
  }): Promise<ContentImage> {
    const { file, organizationId, campaignId, folderId } = params;

    await supabase.rpc('set_current_organization', { org_id: organizationId });

    // 1. Upload to storage port
    const uploadResult = await supabaseContentStorage.uploadImage({
      file,
      organizationId,
      campaignId,
      folderId,
    });

    try {
      // 2. Compute dimensions
      const { width, height, size } = await getImageDimensions(file);

      // 3. Persist metadata row
      const { data, error } = await supabase
        .from('content_images')
        .insert({
          name: file.name,
          file_path: uploadResult.filePath,
          campaign_id: campaignId,
          organization_id: organizationId,
          folder_id: folderId || null,
          file_size: size,
          width,
          height,
          mime_type: uploadResult.mimeType,
        })
        .select()
        .single();

      if (error) {
        // Rollback storage if DB insertion fails
        await supabaseContentStorage.deleteImage(uploadResult.filePath).catch(() => {});
        throw error;
      }

      return mapDbRowToContentImage(data);
    } catch (err) {
      await supabaseContentStorage.deleteImage(uploadResult.filePath).catch(() => {});
      throw err;
    }
  }

  /**
   * Delete image from storage and remove metadata row.
   */
  async deleteImage(
    orgId: string,
    imageId: string,
    filePath: string
  ): Promise<void> {
    await supabase.rpc('set_current_organization', { org_id: orgId });

    // 1. Delete from database
    const { error: dbError } = await supabase
      .from('content_images')
      .delete()
      .eq('id', imageId);

    if (dbError) throw dbError;

    // 2. Delete from storage port
    await supabaseContentStorage.deleteImage(filePath).catch((err) => {
      console.warn('Storage delete warning:', err);
    });
  }

  /**
   * Obtain signed URL for an image.
   */
  async getImageSignedUrl(filePath: string, expiresInSeconds = 3600): Promise<string> {
    return supabaseContentStorage.getSignedUrl(filePath, expiresInSeconds);
  }
}

export const contentGalleryRepository = new ContentGalleryRepository();
